import { useCallback, useMemo, useRef, useState } from 'react';
import { noop } from 'lodash/fp';
import {
  AnimatedPopover,
  Container,
  Content,
  Divider,
  Header,
  Icon,
  InPlaceInput,
  Inline,
  InlineItem,
  LabelText,
  Menu,
  MenuItem,
  MonthPicker,
  Stack,
  Text,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { Layout } from '@/components/layout';
import { GHGProtocolIcon } from '@/components/ghg-protocol-icon';
import { Disclosure, DisclosurePeriod } from '@/types';
import {
  disclosureMonthFromIndex,
  disclosureMonthIndex,
  renderDisclosurePeriod,
} from '@/data/disclosure-month';

// Editable disclosure header. Store-less: takes the current `disclosure`
// and an `onChange` callback that fires on every edit (name keystroke or
// period pick) — the page owns the `localDisclosure` + `hasChanges` state
// and decides when to call the update mutation. Mirrors suit's
// `components/disclosure-header/disclosure-header.tsx`, minus the scenario
// popover (square has no scenario UI in Phase 2).
//
// Two menu rows, mirroring suit's split:
//   1. Disclosure-type label (GHG icon + "Community disclosure") carrying the
//      show/hide-configuration toggle on the right. Clicking the row flips
//      `showConfiguration` (owned by the page via a `?show=` URL param) and
//      collapses/expands the configuration rows below; the right-aligned
//      label flips between "Show configuration" and "Hide configuration".
//   2. Period editor (calendar icon + period text or "Configure period"
//      placeholder) — a configuration row: shown only when
//      `showConfiguration` is on. Clickable; opens an AnimatedPopover over a
//      MonthPicker.
//
// The period editor lives inline here rather than as its own component
// since there's a single consumer; extract when a second disclosure type lands.

type DisclosureHeaderProps = {
  disclosure: Disclosure;
  onChange: (v: Disclosure) => void;
  showConfiguration?: boolean;
  toggleConfiguration?: () => void;
};

type PeriodMenuItemProps = {
  period: DisclosurePeriod | undefined;
  onChange: (v: DisclosurePeriod) => void;
};

const PeriodMenuItem = (props: PeriodMenuItemProps) => {
  const { period, onChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const close = useCallback(() => setIsOpen(false), []);

  const date = useMemo(
    () =>
      period
        ? new Date(period.year, disclosureMonthIndex(period.month))
        : undefined,
    [period],
  );

  const handlePick = useCallback(
    (picked: Date) => {
      onChange({
        year: picked.getFullYear(),
        month: disclosureMonthFromIndex(picked.getMonth()),
      });
      close();
    },
    [onChange, close],
  );

  useOnClickOutside(
    useMemo(() => [ref, popoverRef], []),
    useCallback(() => close(), [close]),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  return (
    <>
      <MenuItem
        ref={ref}
        icon={<Icon magnitude='medium' type='calendar' />}
        content={
          period ? (
            <Text>{renderDisclosurePeriod(period)}</Text>
          ) : (
            <Text tone='secondary'>Configure period</Text>
          )
        }
        onClick={toggle}
      />
      <AnimatedPopover
        stopPropagation
        justify='stretch'
        open={isOpen}
        placement='bottom'
        preventOverflow
        targetRef={ref}
      >
        <Content
          ref={popoverRef}
          borderRadiusSize='large'
          border
          shadow
          background='white'
          padding='medium'
        >
          <Inline alignY='top' space='large'>
            <InlineItem sizing='fill-container'>
              <Content padding='small'>
                <Stack space='medium'>
                  <LabelText>Period end</LabelText>
                  <Text tone='secondary'>
                    Select the ending month for this disclosure. The disclosure
                    period is 12 months up to and including the selected month.
                  </Text>
                </Stack>
              </Content>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <MonthPicker value={date} onChange={handlePick} />
            </InlineItem>
          </Inline>
        </Content>
      </AnimatedPopover>
    </>
  );
};

const DisclosureHeader = (props: DisclosureHeaderProps) => {
  const {
    disclosure,
    onChange,
    showConfiguration = true,
    toggleConfiguration = noop,
  } = props;

  const onChangeName = useCallback(
    (v: string) => {
      onChange({ ...disclosure, name: v });
    },
    [disclosure, onChange],
  );

  const onChangePeriod = useCallback(
    (period: DisclosurePeriod) => {
      onChange({
        ...disclosure,
        definition: { ...disclosure.definition, period },
      });
    },
    [disclosure, onChange],
  );

  // Same structure as suit's disclosure header: a bungle `Container >
  // Header` sitting in the disclosure grid's full-width section, with the
  // content offset left by the sidebar width (239px) so the title lines
  // up with the body beneath it.
  return (
    <Container>
      <Header border>
        <Content background='white' padding='none'>
          <div style={{ paddingLeft: '239px' }}>
            <Layout>
              <InPlaceInput
                value={disclosure.name}
                placeholder='Disclosure name'
                onChange={onChangeName}
                variant='title'
              />
              <Divider />
              <Menu>
                <MenuItem
                  icon={<GHGProtocolIcon />}
                  content={
                    <Inline>
                      <InlineItem sizing='fill-container'>
                        <Text>Community disclosure</Text>
                      </InlineItem>
                      <InlineItem sizing='fit-content'>
                        <Text tone='secondary'>
                          {showConfiguration
                            ? 'Hide configuration'
                            : 'Show configuration'}
                        </Text>
                      </InlineItem>
                    </Inline>
                  }
                  onClick={toggleConfiguration}
                />
                {showConfiguration && (
                  <PeriodMenuItem
                    period={disclosure.definition.period}
                    onChange={onChangePeriod}
                  />
                )}
              </Menu>
            </Layout>
          </div>
        </Content>
      </Header>
    </Container>
  );
};

export type { DisclosureHeaderProps };
export { DisclosureHeader };
